import { CutlistEmailData, CutlistOrder } from '@cutr/constants/cutlist';
import { QueryClient } from '@tanstack/react-query';
import { LoaderFunctionArgs, redirect } from 'react-router-dom';

import { api } from '@/api/backend';
import { cacheMaterials, materialsCached } from '@/api/materials';
import { isCutlistFullyNested } from '@/api/mixedNesting';
import { usePricingStore } from '@/api/pricing';
import { useCutlistState } from '@/api/store';
import { hydrateStoreFn } from '@/hooks';
import { getCutlistQuery } from '@/queries/crud';
import { isStarterTier } from '@/theme';

import { getCutlistEmailQuery } from './agent';

export async function fetchMaterials() {
  const setHasMaterials = useCutlistState.getState().setHasMaterials;
  if (materialsCached()) return;

  await api
    .materials()
    .then(cacheMaterials)
    .then(() => setHasMaterials(true));
}

export const cutlistLoader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const query = getCutlistQuery(params.id as string);
    const hydrateStore = hydrateStoreFn();

    await fetchMaterials();

    // cache hit
    const cutlistCache = queryClient.getQueryData<CutlistOrder>(query.queryKey);
    if (cutlistCache) {
      hydrateStore(cutlistCache);

      return cutlistCache;
    }

    const freshCutlist = await queryClient.fetchQuery(query);
    hydrateStore(freshCutlist);

    return freshCutlist;
  };

export const priceLoader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const query = getCutlistQuery(params.id as string);
    const priceStore = usePricingStore.getState();

    let cutlist =
      queryClient.getQueryData<CutlistOrder>(query.queryKey) ||
      (await queryClient.fetchQuery<CutlistOrder>(query));

    if (isStarterTier() && !isCutlistFullyNested(cutlist)) {
      cutlist = await queryClient.fetchQuery<CutlistOrder>({
        ...query,
        staleTime: 0,
      });
      queryClient.setQueryData(query.queryKey, cutlist);

      if (!isCutlistFullyNested(cutlist)) {
        return redirect(`/admin/cutlists/orders/${params.id}/quote/parts`);
      }
    }

    priceStore.setAmountVat(cutlist.totalAmountInclVAT);
    priceStore.setAmountNoVat(cutlist.totalAmountExclVAT);
    priceStore.setPricing(cutlist.pricingLineItems);
    priceStore.setError(null);

    return cutlist;
  };

export const cutlistEmailLoader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const query = getCutlistEmailQuery(params.id as string);

    // cache hit
    const cutlistEmailCache = queryClient.getQueryData<CutlistEmailData>(
      query.queryKey
    );
    if (cutlistEmailCache) {
      return cutlistEmailCache;
    }

    const freshCutlistEmail = await queryClient.fetchQuery(query);
    return freshCutlistEmail;
  };

export type CutlistLoaderType = Awaited<
  ReturnType<ReturnType<typeof cutlistLoader>>
>;

export type CutlistEmailLoaderType = Awaited<
  ReturnType<ReturnType<typeof cutlistEmailLoader>>
>;
